import React, { useState } from 'react';
import { Icon } from '../Icon';
import './ReminderSelector.css';

interface ReminderSelectorProps {
  reminderTimes?: string[]; // Массив времен
  reminderOffsets?: number[]; // Массив смещений (дней до события)
  remindersEnabled?: boolean;
  transactionType?: 'income' | 'expense'; // Тип транзакции для определения дефолтного состояния
  onChange: (data: { 
    reminderTimes?: string[]; 
    reminderOffsets?: number[];
    remindersEnabled?: boolean 
  }) => void;
  onBack: () => void;
}

const timeOptions = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

const offsetOptions = [
  { value: 0, label: 'В день' },
  { value: 1, label: 'За 1 день' },
  { value: 3, label: 'За 3 дня' },
  { value: 7, label: 'За 7 дней' },
  { value: 10, label: 'За 10 дней' },
];

export const ReminderSelector: React.FC<ReminderSelectorProps> = ({
  reminderTimes = [],
  reminderOffsets = [],
  remindersEnabled,
  transactionType,
  onChange,
  onBack
}) => {
  console.log('🎬 [ReminderSelector] Component initialized with props:', {
    reminderTimes,
    reminderOffsets,
    remindersEnabled,
    transactionType
  });

  // Для доходов по умолчанию выключено, для расходов включено
  const defaultEnabled = transactionType === 'income' ? false : true;
  const [enabled, setEnabled] = useState(remindersEnabled ?? defaultEnabled);
  const [selectedTimes, setSelectedTimes] = useState<string[]>(reminderTimes.length > 0 ? reminderTimes : ['09:00']);
  const [selectedOffsets, setSelectedOffsets] = useState<number[]>(reminderOffsets.length > 0 ? reminderOffsets : [0]);
  const [customTime, setCustomTime] = useState<string>('12:00'); // Для ввода кастомного времени

  console.log('🎬 [ReminderSelector] Initial state set:', {
    selectedTimes,
    selectedOffsets,
    enabled
  });

  const toggleTime = (time: string) => {
    setSelectedTimes(prev => {
      if (prev.includes(time)) {
        // Убираем время, но не даем оставить пустой массив
        const newTimes = prev.filter(t => t !== time);
        return newTimes.length > 0 ? newTimes : prev;
      } else {
        // Добавляем время
        return [...prev, time].sort();
      }
    });
  };

  const toggleOffset = (offset: number) => {
    setSelectedOffsets(prev => {
      if (prev.includes(offset)) {
        // Убираем смещение, но не даем оставить пустой массив
        const newOffsets = prev.filter(o => o !== offset);
        const result = newOffsets.length > 0 ? newOffsets : prev;
        console.log(`🔘 [ReminderSelector] Toggle offset ${offset}: removed. New offsets:`, result);
        return result;
      } else {
        // Добавляем смещение и сортируем
        const result = [...prev, offset].sort((a, b) => a - b);
        console.log(`🔘 [ReminderSelector] Toggle offset ${offset}: added. New offsets:`, result);
        return result;
      }
    });
  };

  const addCustomTime = () => {
    if (customTime && !selectedTimes.includes(customTime)) {
      setSelectedTimes(prev => [...prev, customTime].sort());
      // Сбрасываем поле на дефолтное значение
      setCustomTime('12:00');
    }
  };

  const removeTime = (time: string) => {
    setSelectedTimes(prev => {
      const newTimes = prev.filter(t => t !== time);
      // Не даем оставить пустой массив
      return newTimes.length > 0 ? newTimes : prev;
    });
  };

  // Разделяем времена на стандартные и кастомные
  const customTimes = selectedTimes.filter(t => !timeOptions.includes(t));

  const handleSubmit = () => {
    const reminderData = {
      remindersEnabled: enabled,
      reminderTimes: enabled ? selectedTimes : [],
      reminderOffsets: enabled ? selectedOffsets : []
    };
    
    console.log('📤 [ReminderSelector] Submitting reminder data:', reminderData);
    console.log(`   Total reminders: ${reminderData.reminderOffsets.length} × ${reminderData.reminderTimes.length} = ${reminderData.reminderOffsets.length * reminderData.reminderTimes.length}`);
    
    onChange(reminderData);
  };

  return (
    <div className="reminder-selector step-content">
      <div className="reminder-selector__header">
        <button className="reminder-selector__back neo-raised" onClick={onBack}>
          <Icon name="chevron_left" size={20} />
        </button>
        <div className="reminder-selector__header-content">
          <h2 className="reminder-selector__title">Напоминания</h2>
          <p className="reminder-selector__subtitle">Настройте уведомления</p>
        </div>
      </div>

      <div className="reminder-selector__content">
        <div className="reminder-selector__toggle">
          <div className="toggle-info">
            <div className="toggle-info__title">Включить напоминания</div>
            <div className="toggle-info__subtitle">
              {transactionType === 'income' 
                ? 'Напомним, если нужно' 
                : 'Мы напомним о регулярном платеже'}
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="toggle-switch__slider"></span>
          </label>
        </div>

        {enabled && (
          <>
            {/* Выбор дней до события */}
            <div className="reminder-selector__offsets">
              <div className="time-label">
                Когда напомнить ({selectedOffsets.length} выбрано):
              </div>
              <div className="offset-grid">
                {offsetOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`time-option time-option--compact ${selectedOffsets.includes(option.value) ? 'time-option--active' : ''}`}
                    onClick={() => toggleOffset(option.value)}
                  >
                    {selectedOffsets.includes(option.value) && '✓ '}
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="hint-text">
                Можно выбрать несколько вариантов
              </div>
            </div>

            {/* Выбор времени напоминаний */}
            <div className="reminder-selector__time">
              <div className="time-label">
                Время напоминаний ({selectedTimes.length} выбрано):
              </div>
              <div className="time-grid">
                {timeOptions.map((option) => (
                  <button
                    key={option}
                    className={`time-option time-option--compact ${selectedTimes.includes(option) ? 'time-option--active' : ''}`}
                    onClick={() => toggleTime(option)}
                  >
                    {selectedTimes.includes(option) && '✓ '}
                    {option}
                  </button>
                ))}
              </div>

              {/* Добавление кастомного времени */}
              <div className="custom-time-section" style={{ marginTop: '16px' }}>
                <div className="time-label" style={{ marginBottom: '8px' }}>
                  Или добавьте свое время:
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    className="custom-time-input"
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '12px',
                      border: '2px solid #E5E5EA',
                      fontSize: '16px',
                      fontWeight: '500',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#E5E5EA'}
                  />
                  <button
                    onClick={addCustomTime}
                    className="add-custom-time-btn"
                    style={{
                      padding: '12px 20px',
                      borderRadius: '12px',
                      background: 'transparent',
                      color: selectedTimes.includes(customTime) ? 'var(--color-text-secondary)' : '#007AFF',
                      border: `2px solid ${selectedTimes.includes(customTime) ? 'var(--color-divider)' : '#007AFF'}`,
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: selectedTimes.includes(customTime) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                    disabled={selectedTimes.includes(customTime)}
                  >
                    + Добавить
                  </button>
                </div>
              </div>

              {/* Список кастомных времен */}
              {customTimes.length > 0 && (
                <div className="custom-times-list" style={{ marginTop: '12px' }}>
                  <div className="time-label" style={{ marginBottom: '8px', fontSize: '13px', color: '#8E8E93' }}>
                    Ваши времена:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {customTimes.map((time) => (
                      <div
                        key={time}
                        className="custom-time-chip"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          borderRadius: '20px',
                          background: 'transparent',
                          border: '2px solid #007AFF',
                          color: '#007AFF',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <span>{time}</span>
                        <button
                          onClick={() => removeTime(time)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #007AFF',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#007AFF',
                            padding: 0,
                          }}
                          title="Удалить"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="summary-text" style={{ marginTop: '16px' }}>
                Всего напоминаний: {selectedOffsets.length * selectedTimes.length}
              </div>
            </div>
          </>
        )}
      </div>

      <button
        className="reminder-selector__submit neo-raised"
        onClick={handleSubmit}
      >
        Далее
      </button>
    </div>
  );
};

